import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Styled from './CouponDialog.styled';
import CouponDialogInitial from './components/CouponDialogInitial/CouponDialogInitial';
import CouponDialogForm from './components/CouponDialogForm/CouponDialogForm';
import CouponDialogEmailSuccess from './components/CouponDialogEmailSuccess/CouponDialogEmailSuccess';
import CouponDialogSmsSuccess from './components/CouponDialogSmsSuccess/CouponDialogSmsSuccess';
import RichTextPropTypes from '../../prop-types/rich-text';

/**
 * Coupon Dialog is used to handle the logic for showing the email submission form or success messaging.
 */
const CouponDialog = ({
  couponCode,
  isAutoApplied,
  isDisplayed,
  handleClose,
  discountPercentage,
  smsTermsOfService
}) => {
  const [currentStep, setCurrentStep] = useState({
    name: isAutoApplied ? 'emailForm' : 'initialForm',
    context: null
  });

  useEffect(() => {
    const newCustomerOfferStep =
      sessionStorage.getItem('newCustomerOfferStep') || currentStep.name;
    setCurrentStep({ name: newCustomerOfferStep, context: null });
  }, [isDisplayed]);

  const handleStepChange = (step) => {
    setCurrentStep(step);
    sessionStorage.setItem('newCustomerOfferStep', step.name);
  };

  return (
    <Styled.Modal
      isOpen={isDisplayed}
      onRequestClose={handleClose}
      hasHeaderDivider={false}
      smallLayoutTopMargin={0}
      mediumLayoutTopBottomPadding={false}
      withCollapse
    >
      <Styled.Container>
        {
          {
            initialForm: (
              <CouponDialogInitial
                onComplete={() => handleStepChange({ name: 'emailForm' })}
                onReject={() => {
                  handleClose();
                  handleStepChange({ name: 'emailForm' });
                }}
                isAutoApplied={isAutoApplied}
                discountPercentage={discountPercentage}
              />
            ),
            emailForm: (
              <CouponDialogForm
                onComplete={(ctx) => {
                  handleStepChange({
                    name: 'emailSuccessSmsForm',
                    context: ctx
                  });
                }}
                onReject={handleClose}
                isAutoApplied={isAutoApplied}
                discountPercentage={discountPercentage}
              />
            ),
            emailSuccessSmsForm: (
              <CouponDialogEmailSuccess
                couponCode={couponCode}
                onComplete={() => handleStepChange({ name: 'smsSuccess' })}
                onReject={handleClose}
                isAutoApplied={isAutoApplied}
                discountPercentage={discountPercentage}
                smsTermsOfService={smsTermsOfService}
                {...currentStep.context}
              />
            ),
            smsSuccess: <CouponDialogSmsSuccess />
          }[currentStep.name]
        }
      </Styled.Container>
    </Styled.Modal>
  );
};

CouponDialog.propTypes = {
  /**
   * Coupon code to display to the user.
   */
  couponCode: PropTypes.string.isRequired,
  /**
   * If `true`, display auto applied messaging.
   */
  isAutoApplied: PropTypes.bool,
  /**
   * If `false`, close the modal.
   */
  isDisplayed: PropTypes.bool,
  /**
   * Changes viewToDisplay to BUTTON or NONE by checking cookies on modal close
   */
  handleClose: PropTypes.func.isRequired,
  /**
   * Percentage off value of the discount
   */
  discountPercentage: PropTypes.number,
  /**
   * Contentful object, Settings->RichTextResource for SMS Legal Copy
   */
  smsTermsOfService: PropTypes.shape(RichTextPropTypes)
};

CouponDialog.defaultProps = {
  isAutoApplied: false,
  isDisplayed: false,
  discountPercentage: 20
};

CouponDialog.displayName = 'CouponDialog';

export default CouponDialog;
